import { supabase } from '../../lib/supabase';
import type { OrganizationPixKey, OrganizationPixKeyInput, PixKeyType } from '../../domain/pix/types';

type PixRow = {
  id: string;
  organization_id: string;
  pix_key: string;
  pix_type: string;
  pix_name: string;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
};

export function mapOrganizationPixRow(data: PixRow): OrganizationPixKey {
  return {
    id: data.id,
    organizationId: data.organization_id,
    pixKey: data.pix_key,
    pixType: data.pix_type as PixKeyType,
    pixName: data.pix_name,
    updatedAt: data.updated_at,
  };
}

export class SupabaseOrganizationPixRepository {
  async findByOrganizationId(organizationId: string): Promise<OrganizationPixKey | null> {
    const { data, error } = await supabase
      .from('organization_pix_keys')
      .select('*')
      .eq('organization_id', organizationId)
      .maybeSingle();

    if (error) {
      throw new Error(`Erro ao buscar chave PIX: ${error.message}`);
    }
    return data ? mapOrganizationPixRow(data as PixRow) : null;
  }

  async upsert(
    organizationId: string,
    input: OrganizationPixKeyInput,
    userId: string | null,
  ): Promise<OrganizationPixKey> {
    const now = new Date().toISOString();
    const payload = {
      pix_key: input.pixKey.trim(),
      pix_type: input.pixType,
      pix_name: input.pixName.trim(),
      updated_by: userId,
      updated_at: now,
    };

    const existing = await this.findByOrganizationId(organizationId);

    if (existing) {
      const { data, error } = await supabase
        .from('organization_pix_keys')
        .update(payload)
        .eq('organization_id', organizationId)
        .select('*')
        .single();

      if (error) {
        throw new Error(`Erro ao salvar chave PIX: ${error.message}`);
      }
      return mapOrganizationPixRow(data as PixRow);
    }

    const { data, error } = await supabase
      .from('organization_pix_keys')
      .insert({
        organization_id: organizationId,
        ...payload,
        created_by: userId,
      })
      .select('*')
      .single();

    if (error) {
      throw new Error(`Erro ao salvar chave PIX: ${error.message}`);
    }
    return mapOrganizationPixRow(data as PixRow);
  }

  async deleteByOrganizationId(organizationId: string): Promise<void> {
    const { error } = await supabase
      .from('organization_pix_keys')
      .delete()
      .eq('organization_id', organizationId);

    if (error) {
      throw new Error(`Erro ao remover chave PIX: ${error.message}`);
    }
  }
}

export const organizationPixRepository = new SupabaseOrganizationPixRepository();
